import { put } from 'redux-saga/effects';
import ApiCaller from '../../config/api';
import NotesAction from '../actions/NotesAction';
import { NOTES } from '../../config/constants';

/**
 * Notes sagas.
 *
 * Same shape as `MsgMiddleware`: one generator per action, `res.status == 200` as the
 * success test, a `cb` for callers that need the payload inline.
 *
 * One thing here is unlike every other saga in the app: `NoteUpdate` treats **409 as a
 * first-class outcome**, not a failure. That is the optimistic-concurrency guard firing,
 * and the response body carries the current document so the editor can offer "keep mine /
 * take theirs". Folding it into the failure branch would throw away the only copy of the
 * other person's work.
 */

/**
 * Query string builder. Same rules as MsgMiddleware's: skips empty values so an unused
 * filter does not appear as `?search=`, and escapes what it keeps.
 *
 * `tags` is an ARRAY and is joined rather than repeated — the server accepts both, and one
 * comma-separated value keeps the URL readable in a network log.
 */
const queryString = (params) => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      query.set(key, value.join(','));
      continue;
    }
    query.set(key, String(value));
  }
  const serialised = query.toString();
  return serialised ? `?${serialised}` : '';
};

export default class NotesMiddleware {
  /**
   * A page of notes. `offset: 0` replaces the list, anything higher appends — so one
   * action serves both the first load and infinite scroll, and the reducer decides which
   * without a second action type.
   */
  static *NotesList(params) {
    const { payload, cb } = params;
    const offset = Number(payload?.offset) || 0;
    try {
      const res = yield ApiCaller.Get(
        `/api/v1/notes${queryString({
          tab: payload?.tab,
          search: payload?.search,
          tags: payload?.tags,
          limit: payload?.limit ?? NOTES.PAGE_SIZE,
          offset,
        })}`
      );
      if (res.status == 200) {
        yield put(NotesAction.NotesListSuccess({ ...res?.data, offset }));
        cb && cb(res?.data);
      } else {
        yield put(NotesAction.NotesListFailure());
      }
    } catch {
      yield put(NotesAction.NotesListFailure());
    }
  }

  static *NoteGet(params) {
    const { payload, cb } = params;
    try {
      const res = yield ApiCaller.Get(`/api/v1/notes/${payload?.id}`);
      if (res.status == 200) {
        yield put(NotesAction.NoteGetSuccess(res?.data?.data));
        cb && cb(res?.data);
      } else {
        yield put(NotesAction.NoteGetFailure({ id: payload?.id }));
      }
    } catch (error) {
      // 404 here is ordinary: a note that was deleted, or one shared access has been
      // revoked on. The pane renders "not found" rather than an error toast.
      yield put(
        NotesAction.NoteGetFailure({
          id: payload?.id,
          notFound: error?.response?.status === 404,
        })
      );
    }
  }

  static *NoteCreate(params) {
    const { payload, cb } = params;
    try {
      const res = yield ApiCaller.Post('/api/v1/notes', payload ?? {});
      if (res.status == 200 || res.status == 201) {
        yield put(NotesAction.NoteCreateSuccess(res?.data?.data));
        cb && cb(res?.data);
      } else {
        yield put(NotesAction.NoteCreateFailure());
      }
    } catch {
      yield put(NotesAction.NoteCreateFailure());
    }
  }

  /**
   * Save a note.
   *
   * The 409 branch is the interesting one — see the note at the top of this file. It is
   * reached through the `catch`, because axios rejects any non-2xx, so the conflict body
   * has to be read off `error.response` rather than a resolved response.
   */
  static *NoteUpdate(params) {
    const { payload, cb } = params;
    const { id, ...body } = payload ?? {};
    try {
      const res = yield ApiCaller.Patch(`/api/v1/notes/${id}`, body);
      if (res.status == 200) {
        yield put(NotesAction.NoteUpdateSuccess(res?.data?.data));
        cb && cb(res?.data);
      } else {
        yield put(NotesAction.NoteUpdateFailure({ id }));
      }
    } catch (error) {
      const response = error?.response;
      if (response?.status === 409) {
        yield put(NotesAction.NoteUpdateConflict(response?.data?.data));
        cb && cb(response?.data);
        return;
      }
      yield put(NotesAction.NoteUpdateFailure({ id, message: response?.data?.message }));
    }
  }

  static *NoteDelete(params) {
    const { payload, cb } = params;
    try {
      const res = yield ApiCaller.Delete(`/api/v1/notes/${payload?.id}`);
      if (res.status == 200) {
        yield put(NotesAction.NoteDeleteSuccess({ id: payload?.id }));
        cb && cb(res?.data);
      } else {
        yield put(NotesAction.NoteDeleteFailure());
      }
    } catch {
      yield put(NotesAction.NoteDeleteFailure());
    }
  }

  static *NoteRestore(params) {
    const { payload, cb } = params;
    try {
      const res = yield ApiCaller.Post(`/api/v1/notes/${payload?.id}/restore`, {});
      if (res.status == 200) {
        yield put(NotesAction.NoteRestoreSuccess(res?.data?.data));
        cb && cb(res?.data);
      } else {
        yield put(NotesAction.NoteRestoreFailure());
      }
    } catch {
      yield put(NotesAction.NoteRestoreFailure());
    }
  }

  // ─── tags ─────────────────────────────────────────────────────────────────

  static *NoteTagsList(params) {
    const { cb } = params;
    try {
      const res = yield ApiCaller.Get('/api/v1/notes/tags');
      if (res.status == 200) {
        yield put(NotesAction.NoteTagsListSuccess(res?.data?.data));
        cb && cb(res?.data);
      } else {
        yield put(NotesAction.NoteTagsListFailure());
      }
    } catch {
      yield put(NotesAction.NoteTagsListFailure());
    }
  }

  static *NoteTagCreate(params) {
    const { payload, cb } = params;
    try {
      const res = yield ApiCaller.Post('/api/v1/notes/tags', payload);
      // 200 means it already existed, 201 that it was created. Both are success — the
      // user typed a name and got a tag.
      if (res.status == 200 || res.status == 201) {
        yield put(NotesAction.NoteTagCreateSuccess(res?.data?.data));
        cb && cb(res?.data);
      } else {
        yield put(NotesAction.NoteTagCreateFailure());
      }
    } catch {
      yield put(NotesAction.NoteTagCreateFailure());
    }
  }

  static *NoteTagDelete(params) {
    const { payload, cb } = params;
    try {
      const res = yield ApiCaller.Delete(`/api/v1/notes/tags/${payload?.tag_id}`);
      if (res.status == 200) {
        yield put(NotesAction.NoteTagDeleteSuccess({ tag_id: payload?.tag_id }));
        cb && cb(res?.data);
      } else {
        yield put(NotesAction.NoteTagDeleteFailure());
      }
    } catch {
      yield put(NotesAction.NoteTagDeleteFailure());
    }
  }

  static *NoteSetTags(params) {
    const { payload, cb } = params;
    try {
      const res = yield ApiCaller.Put(`/api/v1/notes/${payload?.id}/tags`, {
        tag_ids: payload?.tag_ids ?? [],
      });
      if (res.status == 200) {
        yield put(NotesAction.NoteSetTagsSuccess(res?.data?.data));
        cb && cb(res?.data);
      } else {
        yield put(NotesAction.NoteSetTagsFailure());
      }
    } catch {
      yield put(NotesAction.NoteSetTagsFailure());
    }
  }

  // ─── sharing ──────────────────────────────────────────────────────────────

  static *NoteSharesList(params) {
    const { payload, cb } = params;
    try {
      const res = yield ApiCaller.Get(`/api/v1/notes/${payload?.id}/shares`);
      if (res.status == 200) {
        yield put(NotesAction.NoteSharesListSuccess(res?.data?.data));
        cb && cb(res?.data);
      } else {
        yield put(NotesAction.NoteSharesListFailure());
      }
    } catch {
      yield put(NotesAction.NoteSharesListFailure());
    }
  }

  static *NoteShareSet(params) {
    const { payload, cb } = params;
    try {
      const res = yield ApiCaller.Put(`/api/v1/notes/${payload?.id}/shares/${payload?.user_id}`, {
        permission: payload?.permission,
      });
      if (res.status == 200 || res.status == 201) {
        yield put(NotesAction.NoteShareSetSuccess(res?.data?.data));
        cb && cb(res?.data);
      } else {
        yield put(NotesAction.NoteShareSetFailure({}));
      }
    } catch (error) {
      // The company-scope refusal arrives as 403 with a sentence the user can act on
      // ("You can only share notes with people in your organisation"), so it is passed
      // through rather than replaced with a generic failure.
      const message = error?.response?.data?.message;
      yield put(NotesAction.NoteShareSetFailure({ message }));
      cb && cb({ success: false, message });
    }
  }

  static *NoteShareRevoke(params) {
    const { payload, cb } = params;
    try {
      const res = yield ApiCaller.Delete(`/api/v1/notes/${payload?.id}/shares/${payload?.user_id}`);
      if (res.status == 200) {
        yield put(NotesAction.NoteShareRevokeSuccess({ user_id: payload?.user_id }));
        cb && cb(res?.data);
      } else {
        yield put(NotesAction.NoteShareRevokeFailure());
      }
    } catch {
      yield put(NotesAction.NoteShareRevokeFailure());
    }
  }

  static *NoteLeave(params) {
    const { payload, cb } = params;
    try {
      const res = yield ApiCaller.Delete(`/api/v1/notes/${payload?.id}/leave`);
      if (res.status == 200) {
        yield put(NotesAction.NoteLeaveSuccess({ id: payload?.id }));
        cb && cb(res?.data);
      } else {
        yield put(NotesAction.NoteLeaveFailure());
      }
    } catch {
      yield put(NotesAction.NoteLeaveFailure());
    }
  }

  /**
   * Who this user can share with.
   *
   * `/chat/company/users` rather than a global search. Note sharing is refused across the
   * company boundary, so a picker offering people outside it would be offering an action
   * guaranteed to fail — and the endpoint's own scope already matches the rule exactly.
   */
  static *NoteShareCandidates(params) {
    const { payload, cb } = params;
    try {
      const res = yield ApiCaller.Get(
        `/api/v1/chat/company/users${queryString({
          limit: payload?.limit ?? 100,
          offset: payload?.offset ?? 0,
        })}`
      );
      if (res.status == 200) {
        yield put(NotesAction.NoteShareCandidatesSuccess(res?.data?.data));
        cb && cb(res?.data);
      } else {
        yield put(NotesAction.NoteShareCandidatesFailure());
      }
    } catch {
      yield put(NotesAction.NoteShareCandidatesFailure());
    }
  }

  // ─── media ────────────────────────────────────────────────────────────────

  /**
   * Upload one image or video.
   *
   * Callback-only: the editor inserts an optimistic placeholder node the moment the file
   * is picked and needs the returned media id to replace it. Nothing goes into the store —
   * media belongs to the document, and the document is the editor's state.
   *
   * The field name `noteMedia` is not incidental. It selects the server's file filter
   * branch, which is images and video minus SVG.
   */
  static *NoteMediaUpload(params) {
    const { payload, cb } = params;
    try {
      const form = new FormData();
      form.append('noteMedia', payload?.file);
      const res = yield ApiCaller.Post(`/api/v1/notes/${payload?.id}/media`, form);
      if (res.status == 200 || res.status == 201) {
        cb && cb({ success: true, data: res?.data?.data });
      } else {
        cb && cb({ success: false });
      }
    } catch (error) {
      cb && cb({ success: false, message: error?.response?.data?.message });
    }
  }
}
